import React, { useEffect, useState, useCallback } from 'react';
import Button from '../common/Button';
import GradientButton from '../common/GradientButton';
import Icon from '../common/Icon';
import StatusBadge from '../common/StatusBadge';
import Input from '../common/Input';
import Pagination from '../common/Pagination';
import Modal from '../common/Modal';
import Card from '../common/Card';
import useAuth from '../../hooks/useAuth';
import {
  getTicketsApi,
  getTicketApi,
  createTicketApi,
  replyToTicketApi,
  updateTicketStatusApi,
} from '../../api/ticket.api';
import {
  TICKET_STATUS_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_PRIORITY_COLORS,
} from '../../utils/constants';
import { toJalaliDateTime } from '../../utils/jalali';

const emptyForm = { subject: '', message: '', priority: 'MEDIUM' };

export default function TicketsView() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ status: '', priority: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [loading, setLoading] = useState(false);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const { data } = await getTicketsApi({ ...clean, page: pagination.page, limit: pagination.limit });
      setTickets(data.data);
      setPagination(prev => ({ ...prev, total: data.meta.total, totalPages: data.meta.totalPages }));
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت تیکت‌ ها');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const openTicket = async (id) => {
    try {
      const { data } = await getTicketApi(id);
      setSelected(data.data);
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت جزئیات');
    }
  };

  const handleCreate = async () => {
    setError('');
    try {
      await createTicketApi(form);
      setSnackbar('تیکت با موفقیت ارسال شد');
      setFormOpen(false);
      setForm(emptyForm);
      loadTickets();
    } catch (err) {
      setError(err.response?.data?.message || 'ارسال تیکت ناموفق بود');
    }
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    try {
      await replyToTicketApi(selected.id, reply);
      setReply('');
      openTicket(selected.id);
      loadTickets();
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'ارسال پاسخ ناموفق بود');
    }
  };

  const handleClose = async () => {
    try {
      await updateTicketStatusApi(selected.id, 'CLOSED');
      setSnackbar('تیکت بسته شد');
      openTicket(selected.id);
      loadTickets();
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'عملیات ناموفق بود');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      OPEN: 'open',
      IN_PROGRESS: 'pending',
      CLOSED: 'inactive',
    };
    return <StatusBadge status={map[status] || 'inactive'} label={TICKET_STATUS_LABELS[status]} />;
  };

  const getPriorityBadge = (priority) => {
    const map = {
      LOW: 'inactive',
      MEDIUM: 'pending',
      HIGH: 'active',
    };
    return <StatusBadge status={map[priority] || 'inactive'} label={TICKET_PRIORITY_LABELS[priority]} />;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-text-dark">تیکت‌ها</h2>
          <p className="text-sm text-slate-400">مدیریت پیام‌ ها و پشتیبانی</p>
        </div>
        <GradientButton onClick={() => setFormOpen(true)}>
          <Icon name="plus" className="ml-2 h-4 w-4" />
          تیکت جدید
        </GradientButton>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">همه وضعیت‌ها</option>
          {Object.entries(TICKET_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
          value={filters.priority}
          onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
        >
          <option value="">همه اولویت‌ ها</option>
          {Object.entries(TICKET_PRIORITY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700 animate-fade-in">
          {snackbar}
          <button className="mr-2" onClick={() => setSnackbar('')}>✕</button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="p-2 md:col-span-1 overflow-y-auto max-h-96">
          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
            </div>
          ) : (
            <>
              {tickets.map((t) => (
                <div
                  key={t.id}
                  onClick={() => openTicket(t.id)}
                  className={`cursor-pointer rounded-xl p-3 transition-all duration-200 hover:bg-emerald-50 ${
                    selected?.id === t.id ? 'bg-emerald-50 border border-emerald-200' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-text-dark">{t.subject}</span>
                    {getStatusBadge(t.status)}
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-slate-500">
                    <span>{t.sender?.fullName}</span>
                    <span>{toJalaliDateTime(t.createdAt)}</span>
                  </div>
                </div>
              ))}
              {tickets.length === 0 && (
                <div className="py-8 text-center text-slate-400">تیکتی وجود ندارد</div>
              )}
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card>

        <div className="md:col-span-2">
          {selected ? (
            <Card>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-lg font-bold text-text-dark">{selected.subject}</h3>
                <div className="flex flex-wrap items-center gap-2">
                  {getPriorityBadge(selected.priority)}
                  {selected.status !== 'CLOSED' && (
                    <Button variant="outline" onClick={handleClose} size="sm">
                      بستن تیکت
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                <span>از طرف: {selected.sender?.fullName}</span>
                <span>•</span>
                <span>نقش: {selected.sender?.role}</span>
                <span>•</span>
                <span>{toJalaliDateTime(selected.createdAt)}</span>
              </div>

              <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm leading-8">
                {selected.message}
              </div>

              {selected.replies?.map((r) => (
                <div
                  key={r.id}
                  className={`mt-3 rounded-xl p-3 text-sm transition-all duration-200 ${
                    r.userId === user.id
                      ? 'bg-emerald-50 mr-8 border-r-4 border-emerald-500'
                      : 'bg-slate-50 ml-8 border-r-4 border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="font-medium">{r.user?.fullName}</span>
                    <span>{toJalaliDateTime(r.createdAt)}</span>
                  </div>
                  <div className="mt-1">{r.message}</div>
                </div>
              ))}

              {selected.status !== 'CLOSED' && (
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <textarea
                    rows="3"
                    className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
                    placeholder="پاسخ شما..."
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                  />
                  <GradientButton size="sm" onClick={handleReply} className="mt-2">
                    <Icon name="send" className="ml-2 h-4 w-4" />
                    ارسال پاسخ
                  </GradientButton>
                </div>
              )}
            </Card>
          ) : (
            <Card className="flex h-64 items-center justify-center text-slate-400">
              یک تیکت را برای مشاهده جزئیات انتخاب کنید
            </Card>
          )}
        </div>
      </div>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title="تیکت جدید" size="md">
        <div className="space-y-4">
          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
          <Input
            label="موضوع"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            required
          />
          <select
            className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
          >
            {Object.entries(TICKET_PRIORITY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-dark/80">متن پیام <span className="mr-1 font-black text-red-500" aria-hidden="true">*</span></label>
            <textarea
              rows="5"
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={() => setFormOpen(false)}>انصراف</Button>
            <GradientButton onClick={handleCreate}>ارسال تیکت</GradientButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}